import { useCallback, useState } from 'react';
import type { Api, SessionItem } from '../api';
import { APPROVAL_LABEL, HISTORY_PAGE_SIZE, SESSION_STATUS_LABEL } from '../admin/labels';
import { fmtDateTime, fmtDuration } from '../admin/format';
import { usePagedList } from '../admin/usePagedList';
import { Pager } from './Pager';

/**
 * 접속 이력(2026-09-15 관리 화면). 서버 `GET /api/sessions`.
 *  - 관리자: 조직의 **모든** 원격 접속(코드로 들어온 익명 접속 포함)
 *  - 일반 사용자: **내가** 접속한 기록만(서버가 걸러 준다 — 화면은 표기만 바꾼다)
 */
export function SessionHistoryView({ api, isAdmin }: { api: Api; isAdmin: boolean }) {
  const [status, setStatus] = useState('');
  const fetchPage = useCallback(
    (offset: number) => api.listSessions({ status: status || undefined, limit: HISTORY_PAGE_SIZE, offset }),
    [api, status],
  );
  const list = usePagedList<SessionItem>(fetchPage);

  return (
    <div className="view">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>{isAdmin ? '접속 이력' : '내 접속 이력'}</h2>
        <div className="row">
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="상태 필터">
            <option value="">모든 상태</option>
            {Object.entries(SESSION_STATUS_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <button className="ghost" onClick={list.reload} disabled={list.loading}>
            {list.loading ? '불러오는 중…' : '새로고침'}
          </button>
        </div>
      </div>
      <p className="hint" style={{ margin: '0 0 14px' }}>
        {isAdmin
          ? '조직 기기에 대한 모든 원격 접속 기록입니다. 기기 코드로 들어온 익명 접속도 포함됩니다.'
          : '내가 원격 접속한 기록입니다.'}
      </p>

      {list.error && <div className="hint" style={{ color: 'var(--err)', marginBottom: 12 }}>{list.error}</div>}
      {list.items.length === 0 && !list.loading ? (
        <p className="hint">기록이 없습니다.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>시작</th>
                <th>기간</th>
                <th>기기</th>
                {isAdmin && <th>접속자</th>}
                <th>결과</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((s) => (
                <tr key={s.sessionId}>
                  <td className="nowrap">{fmtDateTime(s.startTime)}</td>
                  <td className="nowrap">{fmtDuration(s.startTime, s.endTime)}</td>
                  <td>
                    <div>{s.deviceName || '(이름 없음)'}</div>
                    <div className="hint">{s.deviceCode}</div>
                  </td>
                  {isAdmin && (
                    <td>
                      <ViewerCell s={s} />
                    </td>
                  )}
                  <td className="nowrap">
                    <ResultBadge s={s} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager total={list.total} limit={list.limit} offset={list.offset} onChange={list.goTo} disabled={list.loading} />
    </div>
  );
}

/**
 * 접속자 칸. viewer 가 없는 경우가 **둘**이다 — 익명 접속 / 삭제된 사용자. 서버가 이름으로 구분해 주고(`viewerUserName`),
 * 익명이면 추적에 쓰이는 IP 를 함께 보인다.
 */
function ViewerCell({ s }: { s: SessionItem }) {
  if (s.anon) {
    return (
      <>
        <div>
          {s.viewerUserName} <span className="badge">코드 접속</span>
        </div>
        {s.clientIp && <div className="hint">{s.clientIp}</div>}
      </>
    );
  }
  return <div className={s.viewerUserId ? undefined : 'hint'}>{s.viewerUserName}</div>;
}

/** 결과 칸 — 거절·응답 없음은 "종료" 보다 많은 것을 말하므로 승인 결과를 우선한다. */
function ResultBadge({ s }: { s: SessionItem }) {
  const denied = s.approvalStatus === 'rejected' || s.approvalStatus === 'timeout';
  const label = denied
    ? APPROVAL_LABEL[s.approvalStatus ?? ''] ?? s.approvalStatus
    : SESSION_STATUS_LABEL[s.status] ?? s.status;
  const tone = denied ? 'warn' : s.status === 'active' ? 'ok' : '';
  return <span className={`badge ${tone}`}>{label}</span>;
}
